import React, { useMemo } from 'react';
import { Shield, AlertTriangle, AlertCircle, Info, CheckCircle2, BarChart3 } from 'lucide-react';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { useQualityWorkspaceState } from './useQualityWorkspaceState';
export function GovernanceReport() {
  const {
    filteredRecords
  } = useQualityWorkspaceState();
  const rows = useMemo(() => filteredRecords.map(record => ({
    questionId: record.question.id,
    module: record.question.module,
    tier: record.question.tier,
    group: record.question.group,
    issues: record.issues.filter(i => i.category === 'governance')
  })).filter(item => item.issues.length > 0), [filteredRecords]);
  // Rule breakdown — which rules are violated most
  const ruleBreakdown = useMemo(() => {
    const ruleMap = new Map<string, {
      count: number;
      severity: string;
      message: string;
    }>();
    rows.forEach(row => {
      row.issues.forEach(issue => {
        // Extract rule ID from message if present (e.g. "MASTER_2_2")
        const ruleMatch = issue.message.match(/MASTER_\d+_\d+|ADDON_\w+_\d+/);
        const key = ruleMatch ? ruleMatch[0] : issue.message.slice(0, 60);
        const existing = ruleMap.get(key);
        if (existing) {
          existing.count++;
        } else {
          ruleMap.set(key, {
            count: 1,
            severity: issue.severity,
            message: issue.message
          });
        }
      });
    });
    return Array.from(ruleMap.entries()).map(([rule, data]) => ({
      rule,
      ...data
    })).sort((a, b) => b.count - a.count).slice(0, 10);
  }, [rows]);
  const criticalCount = rows.reduce((sum, r) => sum + r.issues.filter(i => i.severity === 'critical').length, 0);
  const warningCount = rows.reduce((sum, r) => sum + r.issues.filter(i => i.severity === 'warning').length, 0);
  return <div className="space-y-4" data-id="element-2455">
      <Card className="p-5" data-id="element-2456">
        <div className="flex items-center justify-between mb-4" data-id="element-2457">
          <div className="flex items-center gap-2" data-id="element-2458">
            <div className="p-2 bg-purple-50 rounded-lg" data-id="element-2459">
              <Shield className="w-5 h-5 text-purple-600" data-id="element-2460" />
            </div>
            <div data-id="element-2461">
              <h3 className="text-lg font-bold text-slate-900" data-id="element-2462">
                Governance Report
              </h3>
              <p className="text-sm text-slate-500" data-id="element-2463">
                {rows.length} questions with governance issues
              </p>
            </div>
          </div>
          <div className="flex gap-2" data-id="element-2464">
            {criticalCount > 0 && <Badge variant="danger" size="sm" data-id="element-2465">
                {criticalCount} critical
              </Badge>}
            {warningCount > 0 && <Badge variant="warning" size="sm" data-id="element-2466">
                {warningCount} warnings
              </Badge>}
            {rows.length === 0 && <Badge variant="success" size="sm" data-id="element-2467">
                All passing
              </Badge>}
          </div>
        </div>

        {/* Rule breakdown summary */}
        {ruleBreakdown.length > 0 && <div data-id="element-2468">
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-2469">
              <BarChart3 className="w-3.5 h-3.5" data-id="element-2470" />
              Most Violated Rules
            </h4>
            <div className="space-y-2" data-id="element-2471">
              {ruleBreakdown.map((item, idx) => <div key={idx} className="flex items-center gap-3" data-id="element-2472">
                  <div className="w-full" data-id="element-2473">
                    <div className="flex items-center justify-between mb-0.5" data-id="element-2474">
                      <span className="text-xs font-medium text-slate-700 truncate max-w-xs" data-id="element-2475">
                        {item.message}
                      </span>
                      <div className="flex items-center gap-2 shrink-0 ml-2" data-id="element-2476">
                        <Badge variant={item.severity === 'critical' ? 'danger' : item.severity === 'warning' ? 'warning' : 'info'} size="sm" data-id="element-2477">
                          {item.severity}
                        </Badge>
                        <span className="text-xs font-bold text-slate-500 w-8 text-right" data-id="element-2478">
                          ×{item.count}
                        </span>
                      </div>
                    </div>
                    <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden" data-id="element-2479">
                      <div className={`h-full rounded-full ${item.severity === 'critical' ? 'bg-rose-400' : item.severity === 'warning' ? 'bg-amber-400' : 'bg-blue-400'}`} style={{
                  width: `${Math.min(100, item.count / (ruleBreakdown[0]?.count || 1) * 100)}%`
                }} data-id="element-2480" />
                    </div>
                  </div>
                </div>)}
            </div>
          </div>}
      </Card>

      {rows.length === 0 ? <Card className="p-12 text-center" data-id="element-2481">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" data-id="element-2482" />
          <h3 className="text-lg font-bold text-slate-900" data-id="element-2483">
            No governance issues
          </h3>
          <p className="text-slate-500" data-id="element-2484">
            All questions pass governance checks in the current filter scope.
          </p>
        </Card> : <div className="space-y-3" data-id="element-2485">
          {rows.slice(0, 100).map(row => <IssueCard key={row.questionId} questionId={row.questionId} module={row.module} tier={row.tier} group={row.group} issues={row.issues} data-id="element-2486" />)}
          {rows.length > 100 && <p className="text-center text-sm text-slate-500 py-2" data-id="element-2487">
              Showing 100 of {rows.length} — use filters to narrow results.
            </p>}
        </div>}
    </div>;
}
function IssueCard({
  questionId,
  module,
  tier,
  group,
  issues
}: {
  questionId: string;
  module: string;
  tier: number;
  group: number;
  issues: Array<{
    id: string;
    severity: string;
    message: string;
  }>;
}) {
  const hasCritical = issues.some(i => i.severity === 'critical');
  return <Card className={`p-4 border-l-4 ${hasCritical ? 'border-l-rose-500' : 'border-l-amber-400'}`} data-id="element-2488">
      <div className="flex items-center justify-between mb-3" data-id="element-2489">
        <code className="text-sm font-bold text-slate-900 bg-slate-100 px-2 py-0.5 rounded" data-id="element-2490">
          {questionId}
        </code>
        <div className="flex gap-1.5" data-id="element-2491">
          <Badge variant="neutral" size="sm" data-id="element-2492">
            {module}
          </Badge>
          <Badge variant="neutral" size="sm" data-id="element-2493">
            T{tier} · G{group}
          </Badge>
        </div>
      </div>
      <ul className="space-y-1.5" data-id="element-2494">
        {issues.map(issue => <li key={issue.id} className="flex items-start gap-2 text-sm" data-id="element-2495">
            <SeverityIcon severity={issue.severity} data-id="element-2496" />
            <span className={issue.severity === 'critical' ? 'text-rose-700' : issue.severity === 'warning' ? 'text-amber-700' : 'text-slate-600'} data-id="element-2497">
              {issue.message}
            </span>
          </li>)}
      </ul>
    </Card>;
}
function SeverityIcon({
  severity
}: {
  severity: string;
}) {
  if (severity === 'critical') return <AlertTriangle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" data-id="element-2498" />;
  if (severity === 'warning') return <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" data-id="element-2499" />;
  return <Info className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" data-id="element-2500" />;
}